import {
  ReactNode, FC, createContext, useContext, useState,
} from 'react';
import {
  XIcon,
} from '@heroicons/react/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { uuid } from '@gltf-transform/core';
import ProgressBar from './ProgressBar';

type ToastType = 'error' | 'info' | 'warning' | 'success';

type Toast = {
  id: string;
  message: ReactNode;
  type: ToastType;
  duration?: number | null;
  timeoutId?: NodeJS.Timeout;
  startTime?: number;
  remainingTime?: number;
  isPaused?: boolean;
  bgColour?: string;
};

type ToastContextType = {
  addToast: (message: ReactNode, options?: { bgColour?: string; duration?: number; type?: ToastType }) => void;
  removeToastsByType: (type: ToastType) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = (id: string) => {
    setToasts((prev) => {
      const toast = prev.find((t) => t.id === id);
      if (toast?.timeoutId) {
        clearTimeout(toast.timeoutId);
      }
      return prev.filter((t) => t.id !== id);
    });
  };

  const addToast = (
    message: ReactNode,
    options?: { type?: ToastType, bgColour?: string, duration?: number },
  ) => {
    const id = uuid();
    const type = options?.type || 'info';

    const duration = options?.duration === null ? null : options?.duration ?? 6000;
    let bgColour = options?.bgColour;
    let timeoutId: NodeJS.Timeout | undefined;
    let startTime: number | undefined;
    let remainingTime: number | undefined;

    switch (type) {
      case 'error':
        bgColour = bgColour || 'bg-fjsRed';
        break;
      case 'warning':
        bgColour = bgColour || 'bg-fjsOrange';
        break;
      case 'info':
        bgColour = bgColour || 'bg-fjsBlue';
        break;
      case 'success':
        bgColour = bgColour || 'bg-fjsGreen';
        break;
      default:
        break;
    }

    if (duration !== null) {
      remainingTime = duration;
      startTime = Date.now();
      timeoutId = setTimeout(() => removeToast(id), duration);
    }
    setToasts((prev) => [...prev, {
      id, message, type, duration, timeoutId, startTime, remainingTime, bgColour,
    }]);
  };

  const pauseToast = (id: string) => {
    setToasts((prev) => prev.map((toast) => {
      if (toast.id !== id || toast.remainingTime === undefined || toast.startTime === undefined) return toast;

      const elapsed = Date.now() - toast.startTime;
      clearTimeout(toast.timeoutId);
      return {
        ...toast,
        remainingTime: toast.remainingTime - elapsed,
        isPaused: true,
      };
    }));
  };

  const resumeToast = (id: string) => {
    setToasts((prev) => prev.map((toast) => {
      if (toast.id !== id || toast.remainingTime === undefined) return toast;

      const startTime = Date.now();
      const timeoutId = setTimeout(() => removeToast(id), toast.remainingTime);
      return {
        ...toast, timeoutId, startTime, isPaused: false,
      };
    }));
  };

  const removeToastsByType = (type: ToastType) => {
    setToasts((prev) => {
      prev
        .filter((t) => t.type === type)
        .forEach((toast) => {
          if (toast.timeoutId) clearTimeout(toast.timeoutId);
        });

      return prev.filter((t) => t.type !== type);
    });
  };

  return (
    <ToastContext.Provider value={{ addToast, removeToastsByType }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col space-y-2 max-w-[16rem] min-w-[12rem] md:max-w-[20rem]">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={`${toast.bgColour ?? 'bg-fjsRed'} shadow-md rounded-lg p-2 pl-3 flex space-x-2.5 text-xs text-fjsWhite relative`}
              onMouseEnter={() => pauseToast(toast.id)}
              onMouseLeave={() => resumeToast(toast.id)}
            >
              <span className="text-pretty leading-relaxed cursor-default whitespace-pre-line text-ellipsis overflow-hidden">{toast.message}</span>
              <button type="button" className="z-[51] rounded-md hover:bg-fjsWhite/25 p-1 h-fit transition ease-in-out duration-200 flex items-center justify-center hover:cursor-pointer" onClick={() => removeToast(toast.id)}>
                <XIcon className="h-4 w-4 md:h-5 md:w-5" />
              </button>
              {/* Animated progress bar */}
              {toast.remainingTime !== undefined && (
                <ProgressBar
                  remainingTime={toast.remainingTime}
                  isPaused={toast.isPaused ?? false}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
